import { addHours, format } from 'date-fns';
import { ProgressBar } from '../../../../../core/components/atoms/ProgressBar';
import { Tag, TagVariant } from '../../../../../core/components/atoms/Tag';
import { Typography } from '../../../../../core/components/molecules/Typography';
import { getDayName } from '../../../../../core/utils/dataTools';
import { getDisciplineName } from '../../../../../core/utils/discipline';
import { isScheduleHubCardClickBlocked } from '../clickGuards';
import MoveContentAction, {
  DisciplineMoveContentParams,
} from './components/MoveContentAction';
import { Icon } from '../../../../../core/components/atoms/Icon';

interface IDisciplineActivityCard {
  dayKey?: string;
  originalDay?: string;
  activityIndex?: number;
  discipline: string;
  progress: number;
  isRevision: boolean;
  isCompleted: boolean;
  compact?: boolean;
  showMoveContentAction?: boolean;
  onMoveContentSuccess?: () => void;
  onMoveContentConfirm?: (
    params: DisciplineMoveContentParams
  ) => Promise<void> | void;
  onCardClick?: (day: string) => void;
  hasEssay?: boolean;
}

const DisciplineActivityCard: React.FC<IDisciplineActivityCard> = ({
  dayKey,
  activityIndex,
  discipline,
  originalDay,
  progress,
  isRevision,
  isCompleted,
  compact = false,
  showMoveContentAction = true,
  onMoveContentSuccess,
  onMoveContentConfirm,
  onCardClick,
  hasEssay,
}) => {
  const handleCardClick = () => {
    if (!onCardClick || isScheduleHubCardClickBlocked()) {
      return;
    }
    const today = new Date();
    const day = getDayName(today.toISOString());
    onCardClick(day);
  };

  const handleShowOriginalDay = () => {
    if (originalDay && dayKey) {
      const isDisciplineOriginalDay = originalDay === dayKey;

      if (isDisciplineOriginalDay) {
        return null;
      }

      return (
        <Typography weight='bold' size='mini' className='italic'>
          {format(addHours(new Date(originalDay), 3), 'dd/MM')}
        </Typography>
      );
    }

    return null;
  };

  const isValidActivityIndex =
    activityIndex === undefined ||
    (Number.isInteger(activityIndex) && activityIndex >= 0);
  const normalizedActivityIndex = isValidActivityIndex
    ? activityIndex
    : undefined;
  const hasDayKey = Boolean(dayKey);
  const hasMoveContentConfirmHandler =
    typeof onMoveContentConfirm === 'function';
  const canShowMoveContentAction =
    Boolean(showMoveContentAction) && hasDayKey && hasMoveContentConfirmHandler;

  return (
    <div
      className='flex flex-col gap-1 rounded-md bg-mono-color-white p-4'
      onClick={handleCardClick}
    >
      <div className='flex flex-row items-center justify-between'>
        <div className='flex cursor-pointer flex-row items-center gap-sm'>
          {isRevision && (
            <Tag variant='neutral' className='text-xs'>
              Revisão
            </Tag>
          )}
          <Tag
            className='text-xs'
            variant={isCompleted ? (discipline as TagVariant) : 'disabled'}
          >
            Concluído
          </Tag>
        </div>

        <div className='flex flex-row items-center gap-xs'>
          {handleShowOriginalDay()}
          {canShowMoveContentAction && (
            <MoveContentAction
              discipline={discipline}
              dayKey={dayKey}
              originalDay={originalDay}
              activityIndex={normalizedActivityIndex}
              onMoveContentSuccess={onMoveContentSuccess}
              onMoveContentConfirm={onMoveContentConfirm}
            />
          )}
        </div>
      </div>
      <div className='flex flex-row items-center justify-between gap-sm'>
        <Typography type='title' size={compact ? 'h6' : 'h5'}>
          {getDisciplineName(discipline)}
        </Typography>
        {hasEssay && (
          <Icon
            Icon={'stylus_note'}
            type='material'
            className='text-md dark:text-card-color-default'
          />
        )}
      </div>
      <ProgressBar
        progress={progress}
        color={`disciplina-${discipline}`}
        backgroundColor='mono-color-lightest'
      />
    </div>
  );
};

export default DisciplineActivityCard;
export type { DisciplineMoveContentParams };
